import React, { useEffect, useState } from 'react';
import { Button } from 'src/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from 'src/components/ui/dialog';
import { clsx } from 'clsx';
import Input from 'src/components/Input/Input';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { array, object } from 'yup';
import { formResolver } from 'src/utils/formResolver';
import { booleanScheme, numberScheme, stringScheme, urlScheme } from 'src/utils/validation-schemes';
import { useParams } from 'react-router-dom';
import { useNotification } from 'src/hooks/useNotification';
import { useUpdateKnowledgeBaseCollectorMutation } from 'src/services/knowledgeBaseService';
import {
  WEBSITE_INITIAL_REQUEST,
  WEBSITE_INITIAL_REQUEST_V2,
  WEBSITE_INTERVAL,
} from 'src/pages/AIKnowledgeBasePage/CurrentKnowledgeBasePage/CurrentKnowledgeBaseConstants';
import { useAppSelector } from 'src/store';
import { useKBContext } from 'src/provider/KnowledgeBaseProvider';
import { API_VERSION } from 'src/constants/api-routes';
import type { Integration } from 'src/utils/fileUtils';
import type { WebsiteFormValues } from 'src/pages/AIKnowledgeBasePage/CurrentKnowledgeBasePage/websiteFormValues';
import { WebsiteCollectorFields } from 'src/pages/AIKnowledgeBasePage/CurrentKnowledgeBasePage/components/WebsiteCollectorFields/WebsiteCollectorFields';
import { useLoaderWhile } from 'src/hooks/useLoaderWhile';
import { getApiVersion } from 'src/utils/apiVersion';

export const EditWebsitePopup = ({
  knowledgeBaseId = null,
  trigger,
  closeSourceModal = () => {},
  item = {},
  disabled = false,
}: {
  knowledgeBaseId?: number | string | null;
  trigger?: React.ReactNode;
  closeSourceModal?: () => void;
  // The website collector to edit, as `data` (the onboarding list wraps its own items this way).
  item?: { data?: Integration };
  disabled?: boolean;
}) => {
  const { setSkipRefetchFiles } = useKBContext();
  const [open, setOpen] = useState(false);
  const { id } = useParams();
  const notification = useNotification();
  const version = getApiVersion();
  const isV1 = version === API_VERSION.V1;

  // V1 keeps the collector's own interval (monthly when it has none); V2 always syncs weekly.
  const getDefaultInterval = () => {
    if (!isV1) return WEBSITE_INTERVAL.WEEKLY;
    return item.data.schedule ? item.data.schedule.interval : WEBSITE_INTERVAL.MONTHLY;
  };

  const getScheduleAutoSync = () => (item.data.schedule ? item.data.schedule.enabled : true);

  const getInitialUrls = () => {
    if (!isV1) return [{ value: item?.data?.url }];

    return item?.data?.urls?.length
      ? item.data.urls.map((url) => {
          return { value: url };
        })
      : [{ value: '' }];
  };
  const [
    updateKnowledgeBaseCollectorMutation,
    { isLoading: isLoadingUpdateKnowledgeBaseCollector },
  ] = useUpdateKnowledgeBaseCollectorMutation();
  const { isTrialPlan } = useAppSelector((s) => s.planSlice);

  const {
    handleSubmit,
    control,
    reset,
    watch,
    formState: { isDirty },
  } = useForm<WebsiteFormValues>({
    defaultValues: {
      urls: item?.data?.urls?.length
        ? item.data.urls.map((url) => {
            return { value: url };
          })
        : [{ value: '' }],
      exclude_urls: item?.data?.exclude_urls?.length
        ? item.data.exclude_urls.map((url) => {
            return { value: url };
          })
        : [],
      name: item.data.name || '',
      autoSync: item.data.schedule ? item.data.schedule.enabled : true,
      recursive: item.data.recursive,
      interval: getDefaultInterval(),
    },
    resolver: formResolver(
      object({
        name: stringScheme({ required: true }).label('Name'),
        urls: array()
          .of(
            object({
              value: urlScheme({ required: true }).label('URL'),
            }),
          )
          .min(1, 'At least one URL is required'),
        exclude_urls: array().of(
          object({
            value: urlScheme({ required: false }).label('Exclude URL'),
          }),
        ),
        recursive: booleanScheme({ required: false }).label('Sync Nested Pages'),
        autoSync: booleanScheme({ required: false }).label('AutoSync'),
        interval: numberScheme({ required: false }).label('Interval'),
      }),
    ),
  });

  const urlFields = useFieldArray({
    control,
    name: 'urls',
  });

  const excludeUrlFields = useFieldArray({
    control,
    name: 'exclude_urls',
  });

  const resetToInitial = (trial = false) => {
    reset({
      urls: getInitialUrls(),
      exclude_urls: item?.data?.exclude_urls?.length
        ? item.data.exclude_urls.map((url) => {
            return { value: url };
          })
        : [],
      name: item.data.name || '',
      autoSync: trial ? !isTrialPlan : getScheduleAutoSync(),
      recursive: item.data.recursive,
      interval: getDefaultInterval(),
    });
  };

  useEffect(() => {
    if (isTrialPlan) {
      resetToInitial(true);
    }
  }, [isTrialPlan]);

  useLoaderWhile(isLoadingUpdateKnowledgeBaseCollector);

  useEffect(() => {
    setSkipRefetchFiles(open);
  }, [open]);

  const submit = (data: WebsiteFormValues) => {
    let body;

    if (isV1) {
      body = {
        ...WEBSITE_INITIAL_REQUEST,
        ...item.data,
        name: data.name,
        urls: data?.urls?.length ? data.urls.map((url) => url.value.trim()) : [],
        exclude_urls: data?.exclude_urls?.length
          ? data.exclude_urls.map((url) => url.value.trim())
          : [],
        recursive: data.recursive,
        schedule: {
          interval: data.interval,
          enabled: data.autoSync,
          period: 'days',
        },
      };
    } else {
      body = {
        ...WEBSITE_INITIAL_REQUEST_V2,
        ...item.data,
        name: data.name,
        url: data.urls[0].value.trim(),
        exclude_urls: data?.exclude_urls?.length
          ? data.exclude_urls.map((url) => url.value.trim())
          : [],
        recursive: data.recursive,
        schedule: {
          interval: data.interval,
          enabled: data.autoSync,
          period: 'days',
        },
      };
    }

    delete body.run_immediately;

    updateKnowledgeBaseCollectorMutation({
      knowledgeBaseId: knowledgeBaseId || id,
      collectorId: item.data.id,
      body,
    }).then((updateKnowledgeBaseCollectorMutationResult) => {
      if (!('error' in updateKnowledgeBaseCollectorMutationResult)) {
        notification.success(`${name} Updated Successfully.`);
        closeSourceModal();
        close();
      }
    });
  };

  const close = () => {
    resetToInitial();
    setOpen(false);
  };

  useEffect(() => {
    if (item && !open) {
      resetToInitial();
    }
  }, [item]);

  const onOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      resetToInitial();
    }

    setOpen(isOpen);
  };

  const name = watch('name');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger
        disabled={disabled}
        asChild
        className={clsx(disabled && 'pointer-events-none opacity-50', 'cursor-pointer')}
      >
        {trigger}
      </DialogTrigger>
      <DialogContent closeButtonHidden className="pb-0">
        <DialogHeader>
          <DialogTitle>Update website</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 pt-4">
          <div className="content">
            <form
              id="update-website-form"
              onSubmit={handleSubmit(submit)}
              className={'flex flex-col gap-5'}
            >
              <div className={'input-container'}>
                <Controller
                  control={control}
                  name="name"
                  render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <Input
                      type={'text'}
                      label={'Name'}
                      placeholder={'CoSupport AI'}
                      invalidMessage={error?.message}
                      value={value}
                      isValid={!error?.message}
                      onChange={onChange}
                    />
                  )}
                />
              </div>
              <WebsiteCollectorFields
                control={control}
                urlFields={urlFields}
                excludeUrlFields={excludeUrlFields}
                excludeButtonLabel="Add Exclude URL"
              />
              <div className="sticky bottom-0 w-full bg-white">
                <div className="flex justify-center my-5">
                  <div className="flex gap-2.5">
                    <Button variant="outline" type="button" className="min-w-24" onClick={close}>
                      Cancel
                    </Button>
                    <Button disabled={!isDirty} type="submit" className="min-w-24">
                      Update
                    </Button>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
